import type { ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useLocalStorage } from '@mantine/hooks';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
  GlobalStyles,
} from '@mui/material';
import { matchPath } from 'react-router';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { brandCssVariables } from 'features/brand/brandCssVariables';
import { brandForPath } from 'features/brand/brandForPath';
import { protocolIdFromPath } from 'features/brand/protocolIdFromPath';
import { RoutePath } from 'features/routing/constants';
import { useAppSelector } from 'features/store/hooks';
import { colorModeStorageKey } from 'features/store/localStorage';
import { getThemeOptions } from './theme';
import { ThemeContext } from './ThemeContext';
import type { PageBrand } from './ThemeContext';
import type { ColorModeStorageValue } from 'features/store/localStorage';

type ThemeProviderProps = { children: ReactNode };

const adminPaths = [
  RoutePath.ProtocolList,
  RoutePath.Brands,
  RoutePath.ProtocolEditWithoutId,
  RoutePath.ProtocolEdit(),
  RoutePath.NotificationTemplatesMatrix(),
  RoutePath.NotificationTemplateEditor(),
];

const isAdminPath = (pathname: string) =>
  adminPaths.some(pattern => matchPath(pattern, pathname) !== null);

// The brand of the first frame: the cached brand of the address
// (`brandForPath`); a participation page of a protocol without a cached brand
// is light until its participation loads and names its brand or none.
const initialPageBrand = (pathname: string): PageBrand =>
  brandForPath(pathname) ??
  (matchPath(RoutePath.ParticipationNotFound, pathname) !== null &&
  protocolIdFromPath(pathname) !== undefined
    ? 'requested'
    : undefined);

export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [mode, setMode] = useLocalStorage<ColorModeStorageValue>({
    key: colorModeStorageKey,
    defaultValue: 'light',
    getInitialValueInEffect: false,
  });
  const user = useAppSelector(selectOptionalUser);

  const [pageBrand, setPageBrand] = useState<PageBrand>(() =>
    initialPageBrand(window.location.pathname)
  );
  const setActiveBrand = useCallback((next: PageBrand) => {
    setPageBrand(prev => {
      if (next === 'requested') return prev ?? next;
      return typeof prev === 'object' && prev.brandId === next?.brandId
        ? prev
        : next;
    });
  }, []);
  const activeBrand = pageBrand === 'requested' ? undefined : pageBrand;

  const darkModeAllowed =
    (user ? user.role === 'admin' : isAdminPath(window.location.pathname)) &&
    pageBrand === undefined;

  const colorMode = useMemo(
    () => ({
      savedMode: mode,
      setColorMode: (newMode: ColorModeStorageValue) => {
        setMode(newMode);
      },
      darkModeAllowed,
      activeBrand,
      setActiveBrand,
    }),
    [mode, setMode, darkModeAllowed, activeBrand, setActiveBrand]
  );

  const effectiveMode = darkModeAllowed ? mode : 'light';

  const theme = useMemo(() => {
    return createTheme(
      getThemeOptions(effectiveMode, activeBrand?.light.tokens)
    );
  }, [effectiveMode, activeBrand]);

  return (
    <ThemeContext.Provider value={colorMode}>
      <MuiThemeProvider theme={theme}>
        <GlobalStyles
          // https://v5.mui.com/material-ui/react-text-field/#performance
          styles={{
            '@keyframes mui-auto-fill': { from: { display: 'block' } },
            '@keyframes mui-auto-fill-cancel': { from: { display: 'block' } },
          }}
        />
        {activeBrand && (
          <GlobalStyles
            styles={{ ':root': brandCssVariables(activeBrand.light.tokens) }}
          />
        )}
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
